import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { BulkActionBanner } from '@/lib/crm/list/components/BulkActionBanner';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { upsertObject } from '@/lib/crm/store/crmObjectStore';
import {
  type ActiveSingleObjectDeleteJob,
  clearSingleObjectDeleteJob,
  currentSingleObjectDeleteJobGeneration,
  setActiveSingleObjectDeleteJobOrg,
  useActiveSingleObjectDeleteJobs,
} from '@/lib/crm/store/singleObjectDeleteJobStore';
import { logger } from '@/lib/logger';
import type { BulkActionJob, EntityTypeName } from '@/types/crm';

/**
 * Banner + terminal handling for a single in-flight async single-object-delete
 * job. Extracted so each concurrent job owns its own callbacks (a shared
 * closure over "the active job" would misfire when two are in flight).
 */
function SingleObjectDeleteJobBanner({
  job,
  stackIndex,
}: {
  job: ActiveSingleObjectDeleteJob;
  stackIndex: number;
}) {
  const queryClient = useQueryClient();

  // A job's side effects — record restore, terminal toast, list invalidation —
  // must only touch the session that dispatched it. An org switch/logout bumps
  // the store's generation; if it has advanced past this job's captured value, a
  // session boundary was crossed while the poll/fetch was in flight, so the
  // response belongs to a prior session (even the same org after returning to
  // it) and must be dropped. Re-check live — not a value captured when the
  // callback was created — because these callbacks can resolve after the banner
  // has unmounted (navigation / the store dropping the job on switch).
  const isJobCurrent = useCallback(
    () => job.dispatchGeneration === currentSingleObjectDeleteJobGeneration(),
    [job.dispatchGeneration]
  );

  const handleDismiss = useCallback(() => {
    clearSingleObjectDeleteJob(job.jobId);
  }, [job.jobId]);

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: crmQueryKeys.objects(
        job.orgId,
        job.entityTypeName as EntityTypeName
      ),
    });
  }, [queryClient, job.orgId, job.entityTypeName]);

  // Reconcile the optimistically-hidden record with server truth: re-fetch it
  // and upsert. If the record really was deleted, getByEntityId 404s and we
  // leave it hidden; if it still exists (delete failed/cancelled/never applied)
  // the upsert clears the optimistic `deleted_at` and the row returns.
  const restoreRecord = useCallback(async () => {
    try {
      const fresh = await createCRMObjectClient(
        job.orgId,
        job.entityTypeName
      ).getByEntityId(job.entityId);
      // An org switch/logout may have happened while the fetch was in flight. The
      // switch flushes the shared CRM store to prevent cross-org data and bumps
      // the session generation, so re-inserting this prior-session record now
      // would leak it into the newly-active session — even in the same org after
      // returning to it. Only apply the restore if this job is still current.
      if (!isJobCurrent()) {
        return;
      }
      upsertObject(fresh);
    } catch (err) {
      logger.warn('Failed to restore record after non-completed async delete', {
        component: 'crm',
        entity_id: job.entityId,
        error: String(err),
      });
    }
  }, [job.orgId, job.entityTypeName, job.entityId, isJobCurrent]);

  const handleTerminal = useCallback(
    async (bulkJob: BulkActionJob) => {
      if (bulkJob.status === 'completed') {
        // The optimistic removal was correct — nothing to reconcile. Confirm to
        // the user (and refresh the list) only if this job still belongs to the
        // live session; a prior session's terminal must not toast the new one.
        if (isJobCurrent()) {
          toast.success(`${job.recordName} deleted`);
          invalidateList();
        }
        return;
      }
      // Non-completed terminal states (failed, completed_with_errors,
      // cancelled) all leave the record un-deleted, so roll back the optimistic
      // removal. restoreRecord is a no-op once the session has moved on (its own
      // generation guard); re-check here before notifying so a superseded
      // session's outcome can't toast or refetch the live one. Cancelled is an
      // expected outcome — surface it as a neutral cancellation, not a failure.
      await restoreRecord();
      if (!isJobCurrent()) {
        return;
      }
      if (bulkJob.status === 'cancelled') {
        toast.success(`${job.recordName} deletion cancelled`);
      } else {
        toast.error(`We couldn't delete ${job.recordName}. Try again.`);
      }
      invalidateList();
    },
    [job.recordName, restoreRecord, invalidateList, isJobCurrent]
  );

  // Expired/unavailable job (404) before we ever saw a terminal status. The
  // outcome is unknown, so reconcile with the server and surface an error
  // rather than silently leaving the row optimistically hidden — but only for
  // the live session (an org switch/logout supersedes this job).
  const handleNotFound = useCallback(async () => {
    await restoreRecord();
    if (!isJobCurrent()) {
      return;
    }
    invalidateList();
    toast.error(`Couldn't confirm deletion of ${job.recordName}.`);
  }, [job.recordName, restoreRecord, invalidateList, isJobCurrent]);

  return (
    <BulkActionBanner
      orgId={job.orgId}
      activeJobId={job.jobId}
      totalCount={job.totalCount}
      entityTypeName={job.entityTypeName}
      stackIndex={stackIndex}
      suppressTerminalToast
      onTerminal={handleTerminal}
      onNotFound={handleNotFound}
      onDismiss={handleDismiss}
    />
  );
}

/**
 * App-level host for the async single-object-delete progress banners.
 *
 * Mounted once in `TeamProvider` so a banner survives navigation away from the
 * record that was deleted (the record's own slideout/page unmounts as soon as
 * the delete is dispatched). Renders one banner per in-flight job, scoped to
 * the active org so a delete pending in a previous org neither polls nor toasts
 * after an org switch.
 */
export function SingleObjectDeleteJobHost() {
  const jobs = useActiveSingleObjectDeleteJobs();
  const { currentOrg } = useOrg();
  const currentOrgId = currentOrg?.id;

  // Keep the store's active org in sync with the live org. On an org switch this
  // drops jobs from the previous org (and blocks late 202s for it); on logout
  // (no active org) it clears ALL tracked jobs, so a later session in the same
  // org can't reattach to a prior user's stale job.
  useEffect(() => {
    setActiveSingleObjectDeleteJobOrg(currentOrgId ?? null);
  }, [currentOrgId]);

  return (
    <>
      {jobs
        .filter(job => job.orgId === currentOrgId)
        .map((job, index) => (
          <SingleObjectDeleteJobBanner
            key={job.jobId}
            job={job}
            stackIndex={index}
          />
        ))}
    </>
  );
}
